?<template>
  <div class="report-diary">
    <div class="report-diary__toolbar">
      <div class="report-diary__title">
        <span
          class="btn new-content-data-item-toggle-circle"
          @click="cancelReport()"
        >
          <span class="sienit">e</span>
        </span>
        <h2>{{ pageTitle }}</h2>
      </div>
      <ReportButtons
        :allow-save="allowSave"
        :can-export="!!report._id"
        :is-exporting-pdf="isExportingPdf"
        @export-pdf="exportPdf"
        @cancel="cancelReport"
        @save="save"
      />
    </div>

    <div class="report-diary__card">
      <section class="report-diary__top-card">
        <div class="report-diary__top-main">
          <div>
            <div class="report-diary__heading">
              <h1>Дневник на обект</h1>
              <div class="report-diary__status" v-if="report.isInvalid">
                Грешен отчет
              </div>
            </div>
            <div class="report-diary__meta">
              <div class="report-diary__form-grid">
                <BaseField
                  :value="report.company.companyName"
                  label="Проект"
                  :options="companies"
                  option-label="companyName"
                  placeholder="Изберете проект"
                  :selected="!!report.company._id"
                  @focus="getCompanies"
                  @select="selectCompany"
                  @clear="resetSelectedCompany"
                />
                <BaseField
                  :value="report.project.projectName"
                  label="Обект"
                  :options="projects"
                  option-label="projectName"
                  placeholder="Изберете обект"
                  :disabled="!report.company._id"
                  :selected="!!report.project._id"
                  @focus="getProjects"
                  @select="selectProject"
                  @clear="resetSelectedProject"
                />
                <BaseField
                  :value="report.object.objectName"
                  label="Подобект"
                  :options="objects"
                  option-label="objectName"
                  placeholder="Изберете подобект"
                  :disabled="!report.project._id"
                  :selected="!!report.object._id"
                  @focus="getObjects"
                  @select="selectObject"
                  @clear="resetSelectedObject"
                />
                <BaseField
                  :value="report.part && report.part.partName"
                  label="Част"
                  :options="parts"
                  option-label="partName"
                  placeholder="Изберете част"
                  :disabled="!report.object._id"
                  :selected="!!(report.part && report.part._id)"
                  @focus="getParts"
                  @select="selectPart"
                  @clear="resetSelectedPart"
                />
              </div>
              <BaseDateField
                :value="report.date"
                label="Дата"
                @input="setReportDate"
              />
            </div>
          </div>

          <div class="report-diary__weather">
            <div class="report-diary__weather-header">
              <strong>Климатични условия</strong>
              <label>
                Температура:
                <input
                  type="number"
                  v-model="report.weather.temperature"
                  @input="reportChanged"
                />
                °C
              </label>
            </div>
            <button
              class="report-diary__weather-auto"
              type="button"
              :disabled="isFetchingWeather"
              @click="fetchWeatherForecast"
            >
              <img :src="locationIcon" alt="" />
              {{
                isFetchingWeather
                  ? "Засичане..."
                  : "Засечи автоматично от локацията на обекта"
              }}
            </button>
            <div class="report-diary__weather-options">
              <button
                v-for="option in weatherOptions"
                :key="option.key"
                class="report-diary__weather-option"
                :class="{
                  'report-diary__weather-option--active':
                    report.weather.condition === option.key,
                }"
                type="button"
                @click="setWeatherCondition(option.key)"
              >
                <span>{{ option.label }}</span>
                <img :src="option.image" :alt="option.label" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <div class="report-diary__automation-actions">
        <button
          class="btn"
          type="button"
          :disabled="!hasSelectedLocation"
          @click="openInvoiceScanner"
        >
          <img
            class="icon"
            src="@/assets/img/report/invoice-upload-read.svg"
            alt=""
          />
          <span>Ъплоуд и разчитане на фактури - за автоматично отчитане</span>
        </button>
        <button
          class="btn btn--voice-report"
          type="button"
          :disabled="!hasSelectedLocation"
          @click="openAiReportModal"
        >
          <img
            class="icon"
            src="../assets/img/report/voice-report.svg"
            alt=""
          />
          <span>Отчитане с глас, снимка или свободен текст</span>
        </button>
      </div>

      <transition-group name="fade" tag="div" class="report-diary__groups">
        <section
          class="report-diary__section"
          :class="`report-diary__section--${group.theme}`"
          v-for="group in visibleSectionGroups"
          :key="group.key"
        >
          <button
            class="report-diary__section-header"
            :class="`report-diary__section-header--${group.theme}`"
            type="button"
            @click="toggleGroup(group.key)"
          >
            <span>{{ group.title }}</span>
            <span class="report-diary__collapse-toggle">{{
              isGroupCollapsed(group.key) ? "+" : "-"
            }}</span>
          </button>

          <div v-show="!isGroupCollapsed(group.key)">
            <div
              class="report-diary__table-wrap"
              :class="`report-diary__table-wrap--${group.theme}`"
              v-for="section in group.sections"
              :key="section.key"
            >
              <button
                class="report-diary__table-title"
                type="button"
                @click="toggleSection(section.key)"
              >
                <strong>{{ section.title }}</strong>
                <span
                  class="report-diary__collapse-toggle report-diary__collapse-toggle--inner"
                  >{{ isSectionCollapsed(section.key) ? "+" : "-" }}</span
                >
              </button>
              <div
                v-show="!isSectionCollapsed(section.key)"
                class="report-diary__table-content"
              >
                <table class="report-diary__table">
                  <thead>
                    <tr>
                      <th class="report-diary__table-number">№</th>
                      <th
                        v-for="(field, fieldIndex) in visibleSectionFields(
                          section
                        )"
                        :key="field.key"
                        :class="{
                          'report-diary__table-main-field': fieldIndex === 0,
                        }"
                      >
                        {{ field.label }}
                      </th>
                      <th class="report-diary__table-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in report.diary[section.key]"
                      :key="`${section.key}-${index}`"
                      class="report-diary__data-row"
                    >
                      <td class="report-diary__table-number" data-label="Ред">
                        {{ index + 1 }}
                      </td>
                      <td
                        v-for="(field, fieldIndex) in visibleSectionFields(
                          section
                        )"
                        :key="field.key"
                        :class="{
                          'report-diary__table-main-field': fieldIndex === 0,
                        }"
                        :data-label="field.label"
                      >
                        <a
                          v-if="
                            field.key === 'documentNumber' &&
                            invoiceSearchUrl(row[field.key])
                          "
                          class="report-diary__invoice-link"
                          :href="invoiceSearchUrl(row[field.key])"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ row[field.key] }}
                        </a>

                        <template v-else-if="field.calculated">
                          {{ calculatedDiaryValue(row, field) }}
                        </template>

                        <BaseField
                          v-else
                          hide-icon
                          field-class="base-field--table-input"
                          :type="field.type || 'text'"
                          :min="field.type === 'number' ? 0 : null"
                          :value="row[field.key]"
                          :options="fieldSuggestions(section.key, field.key)"
                          :placeholder="field.placeholder || ''"
                          @input="
                            setDiaryValue(section.key, index, field.key, $event)
                          "
                        />
                      </td>
                      <td
                        class="report-diary__table-actions"
                        data-label="Действия"
                      >
                        <button
                          class="sienit report-diary__table-remove"
                          type="button"
                          @click="removeRow(section.key, index)"
                        >
                          b
                        </button>
                      </td>
                    </tr>
                    <tr
                      v-if="!report.diary[section.key].length"
                      class="report-diary__empty-data-row"
                    >
                      <td
                        :colspan="visibleSectionFields(section).length + 2"
                        class="report-diary__empty-row"
                      >
                        Няма добавени редове.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <button
                  class="btn report-diary__add-row"
                  type="button"
                  @click="addRow(section.key)"
                >
                  + Добави нов ред
                </button>
              </div>
            </div>
          </div>
        </section>
      </transition-group>

      <section class="report-diary__section report-diary__signature">
        <div class="report-diary__signature-author">
          <h3>Съставил</h3>
          <label>Отчетник</label>
          <p>{{ report.createdBy.name }}</p>
        </div>
        <button
          class="report-diary__signature-box"
          type="button"
          :disabled="!allowSave"
          @click="openSignatureModal"
        >
          <img v-if="report.signature" :src="report.signature" alt="Подпис" />
          <span v-else>Добави подпис</span>
        </button>
      </section>

      <ReportButtons
        style="justify-content: end"
        :allow-save="allowSave"
        :can-export="!!report._id"
        :is-exporting-pdf="isExportingPdf"
        @export-pdf="exportPdf"
        @cancel="cancelReport"
        @save="save"
      />
    </div>

    <transition name="fade">
      <UnsavedChangesModal v-if="isUnsavedChangesModalOpened" />
    </transition>
    <transition name="fade">
      <SignatureModal
        v-if="isSignatureModalOpened"
        :signature="report.signature"
        @close="closeSignatureModal"
        @signed="setSignature"
      />
    </transition>
    <transition name="fade">
      <AiReportModal
        v-if="isAiReportModalOpened"
        :item-suggestions="aiMaterialSuggestions"
        :unit-suggestions="fieldSuggestions('buildingMaterials', 'unit')"
        @close="closeAiReportModal"
        @result="applyAiReportResult"
      />
    </transition>
    <transition name="fade">
      <Modal
        v-if="isInvoiceScannerOpened"
        classes="full report-diary__scanner-modal"
        :bypass-keydown-events="isInvoiceScanProcessing"
        @close="closeInvoiceScanner"
      >
        <button
          class="report-diary__scanner-close"
          type="button"
          aria-label="Затвори"
          :disabled="isInvoiceScanProcessing"
          @click="closeInvoiceScanner"
        >
          ×
        </button>
        <InvoiceScanningView
          :locked-location="invoiceLocation"
          @invoice-accepted="addInvoiceToReport"
          @processing-change="isInvoiceScanProcessing = $event"
        />
      </Modal>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AiReportModal from "./shared/AiReportModal";
import BaseDateField from "./shared/BaseDateField";
import BaseField from "./shared/BaseField";
import SignatureModal from "./shared/SignatureModal";
import UnsavedChangesModal from "./shared/UnsavedChangesModal";
import log from "./mixins/log.js";
import ReportButtons from "./ReportButtons.vue";
import InvoiceScanningView from "./InvoiceScanningView.vue";
import Modal from "./components/Modal.vue";
import { findAiLocationMatch } from "../utils/aiLocationMatcher";

const emptyObjectLocation = () => ({
  latitude: "",
  longitude: "",
});

const emptyWeather = () => ({
  location: emptyObjectLocation(),
  condition: "",
  temperature: "",
  capturedAt: "",
});

const diarySections = [
  {
    key: "buildingMaterials",
    title: "1. Вложен и върнат материал",
    fields: [
      { key: "name", label: "Вид на материала" },
      { key: "documentNumber", label: "Чрез документ" },
      { key: "unit", label: "Ед. м." },
      { key: "incomingQuantity", label: "Влож. колич.", type: "number" },
      { key: "source", label: "Върн. м-л склад" },
      { key: "place", label: "М-ли за др. обект" },
      { key: "note", label: "Забележка" },
      {
        key: "price",
        label: "Цена (евро)",
        type: "number",
        requiresPriceRole: true,
      },
      {
        key: "totalSum",
        label: "Обща сума (евро)",
        calculated: "sum",
        quantityKey: "incomingQuantity",
        requiresPriceRole: true,
      },
    ],
  },
  {
    key: "movedInventory",
    title: "2. Преместен инвентар",
    fields: [
      { key: "name", label: "Вид инвентар / инструмент" },
      { key: "documentNumber", label: "Чрез документ" },
      { key: "unit", label: "Ед. м" },
      { key: "quantity", label: "Количество", type: "number" },
      { key: "direction", label: "Направление на преместването" },
      { key: "through", label: "Чрез кого" },
      { key: "note", label: "Забележка" },
      {
        key: "price",
        label: "Цена (евро)",
        type: "number",
        requiresPriceRole: true,
      },
      {
        key: "totalSum",
        label: "Обща сума (евро)",
        calculated: "sum",
        quantityKey: "quantity",
        requiresPriceRole: true,
      },
    ],
  },
  {
    key: "consumedWithoutFinalResult",
    title: "3. Развитие на СМР с отчитане на крайния резултат",
    fields: [
      { key: "workType", label: "Вид СМР" },
      { key: "unit", label: "Ед. м." },
      { key: "totalQuantity", label: "Общо колич.", type: "number" },
      { key: "dailyCompletion", label: "Изпълнено за деня", type: "number" },
      {
        key: "remaining",
        label: "Остатък",
        type: "number",
        calculated: "difference",
        baseKey: "totalQuantity",
        quantityKey: "dailyCompletion",
        showZero: true,
      },
      { key: "note", label: "Забележка" },
      {
        key: "price",
        label: "Цена (евро)",
        type: "number",
        requiresPriceRole: true,
      },
      {
        key: "totalSum",
        label: "Обща сума (евро)",
        calculated: "sum",
        quantityKey: "dailyCompletion",
        requiresPriceRole: true,
      },
    ],
  },
  {
    key: "receivedInventory",
    title: "1. Получен инвентар на обекта",
    fields: [
      { key: "name", label: "Вид на инвентара" },
      { key: "documentNumber", label: "Чрез документ" },
      { key: "unit", label: "Ед. м" },
      { key: "quantity", label: "Количество", type: "number" },
      { key: "note", label: "Забележка" },
      {
        key: "price",
        label: "Цена (евро)",
        type: "number",
        requiresPriceRole: true,
      },
      {
        key: "totalSum",
        label: "Обща сума (евро)",
        calculated: "sum",
        quantityKey: "quantity",
        requiresPriceRole: true,
      },
    ],
  },
  {
    key: "personnel",
    title: "2. Персонал на обекта",
    fields: [
      { key: "person", label: "Персонал" },
      { key: "position", label: "Длъжност" },
      { key: "count", label: "Брой", type: "number" },
      { key: "note", label: "Забележка" },
    ],
  },
  {
    key: "mechanizations",
    title: "3. Използвана механизация на обекта",
    fields: [
      { key: "type", label: "Вид техника и регистр.№" },
      { key: "shifts", label: "М-ни Смени" },
      { key: "shiftsOutside", label: "М-ни външни" },
      { key: "hoursWorked", label: "Часове работа", type: "number" },
      { key: "courses", label: "Курсове", type: "number" },
      { key: "note", label: "Забележка" },
    ],
  },
];

const emptyDiary = () =>
  diarySections.reduce((diary, section) => {
    diary[section.key] = [];
    return diary;
  }, {});

export default {
  components: {
    AiReportModal,
    BaseDateField,
    BaseField,
    SignatureModal,
    UnsavedChangesModal,
    ReportButtons,
    InvoiceScanningView,
    Modal,
  },
  mixins: [log],
  data() {
    return {
      companies: [],
      projects: [],
      objects: [],
      parts: [],
      selectedObject: undefined,
      reportSuggestions: {},
      collapsedGroups: {},
      collapsedSections: {},
      isFetchingWeather: false,
      locationIcon: require("../assets/img/report/location.svg"),
      weatherOptions: [
        {
          key: "rain",
          label: "Дъжд",
          image: require("../assets/img/report/rain.png"),
        },
        {
          key: "snow",
          label: "Сняг",
          image: require("../assets/img/report/snow.png"),
        },
        {
          key: "cloudy",
          label: "Облачно",
          image: require("../assets/img/report/cloudy.png"),
        },
        {
          key: "sunny",
          label: "Слънчево",
          image: require("../assets/img/report/sunny.png"),
        },
      ],
      allowSave: true,
      initialObjectId: "",
      isComingFromOutside: null,
      isReportChanged: false,
      isExportingPdf: false,
      isAiReportModalOpened: false,
      isSignatureModalOpened: false,
      isUnsavedChangesModalOpened: false,
      isInvoiceScannerOpened: false,
      isInvoiceScanProcessing: false,
      report: this.createReport(),
    };
  },
  computed: {
    ...mapGetters(["getUser"]),
    pageTitle() {
      return this.$route.name === "editReport"
        ? "Редактиране на дневник на обект"
        : "Нов отчет > Дневник на обект";
    },
    reportDateInput: {
      get() {
        if (!this.report.date) return "";
        return this.$moment(this.report.date).format("YYYY-MM-DD");
      },
      set(value) {
        this.report.date = value ? new Date(`${value}T12:00:00`) : new Date();
      },
    },
    sectionGroups() {
      return [
        {
          key: "costs",
          title: "Част разходи",
          theme: "expense",
          sections: diarySections.slice(0, 3),
        },
        {
          key: "income",
          title: "Част приходи",
          theme: "income",
          sections: diarySections.slice(3),
        },
      ];
    },
    visibleSectionGroups() {
      return this.report.object._id ? this.sectionGroups : [];
    },
    hasSelectedLocation() {
      return Boolean(
        this.report.company._id &&
          this.report.project._id &&
          this.report.object._id
      );
    },
    aiMaterialSuggestions() {
      const catalog =
        this.selectedObject && Array.isArray(this.selectedObject.materials)
          ? this.selectedObject.materials
          : [];
      const history = this.reportSuggestions["buildingMaterials.name"] || [];
      const seen = new Set();

      return [
        ...catalog.map((item) => ({
          name: item.name || "",
          unit: item.unit || "",
          price: item.price === undefined || item.price === null
            ? ""
            : item.price,
          suggestionOrigin: "catalog",
        })),
        ...history.map((name) => ({
          name,
          unit: "",
          price: "",
          suggestionOrigin: "history",
        })),
      ].filter((item) => {
        const normalized = String(item.name || "").trim().toLowerCase();
        if (!normalized || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      });
    },
    invoiceLocation() {
      return {
        company: this.report.company,
        project: this.report.project,
        object: this.report.object,
      };
    },
    hasObjectLocation() {
      const location = this.report.weather.location || emptyObjectLocation();
      return !!(location.latitude && location.longitude);
    },
    objectLocationText() {
      const location = this.report.weather.location || emptyObjectLocation();
      return this.hasObjectLocation
        ? `${location.latitude},${location.longitude}`
        : "не е избрана";
    },
  },
  methods: {
    openAiReportModal() {
      if (!this.hasSelectedLocation) {
        this.$snotify.error("Изберете проект, обект и подобект.");
        return;
      }

      this.isAiReportModalOpened = true;
    },
    closeAiReportModal() {
      this.isAiReportModalOpened = false;
    },
    applyAiReportResult(result) {
      if (!this.hasSelectedLocation) {
        this.$snotify.error("Изберете проект, обект и подобект.");
        this.closeAiReportModal();
        return;
      }

      const sourceDiary =
        result && result.diary && typeof result.diary === "object"
          ? result.diary
          : {};
      let addedRows = 0;

      diarySections.forEach((section, sectionIndex) => {
        const sourceRows = Array.isArray(sourceDiary[section.key])
          ? sourceDiary[section.key]
          : [];
        const rows = sourceRows
          .map((row) => this.createAiReportRow(section, row))
          .filter((row) => this.hasDiaryRowValue(section, row));

        if (!rows.length) return;

        this.report.diary[section.key].push(...rows);
        this.$set(
          this.collapsedGroups,
          sectionIndex < 3 ? "costs" : "income",
          false
        );
        this.$set(this.collapsedSections, section.key, false);
        addedRows += rows.length;
      });

      const warnings = [
        ...(Array.isArray(result && result.warnings)
          ? result.warnings
          : []),
        ...(Array.isArray(result && result.unparsedText)
          ? result.unparsedText.map((text) => `Неразпознато: ${text}`)
          : []),
      ].filter(Boolean);

      if (!addedRows) {
        this.$snotify.warning(
          warnings[0] || "Не бяха намерени данни за зареждане в отчета."
        );
        return;
      }

      this.reportChanged();
      this.closeAiReportModal();
      this.$snotify.success(`Добавени са ${addedRows} реда от AI отчета.`);

      if (warnings.length) {
        this.$snotify.warning(warnings.slice(0, 3).join(" • "));
      }
    },
    async applyAiReportLocation(sourceLocation) {
      const location =
        sourceLocation && typeof sourceLocation === "object"
          ? sourceLocation
          : {};
      const warnings = [];
      const candidate = {
        company: this.report.company,
        project: this.report.project,
        object: this.report.object,
        part: this.report.part,
      };
      const loadedOptions = {
        companies: null,
        projects: null,
        objects: null,
        parts: null,
      };
      let matchedCount = 0;
      let resolutionFailed = false;
      const hasChanged = (current, next) =>
        String((current && current._id) || "") !==
        String((next && next._id) || "");

      try {
        if (location.project) {
          loadedOptions.companies = await this.fetchAiLocationOptions(
            `companies/${this.getUser._id}`
          );
          const result = findAiLocationMatch(
            location.project,
            loadedOptions.companies,
            "companyName",
            "проект"
          );

          if (result.match) {
            if (hasChanged(candidate.company, result.match)) {
              candidate.project = { _id: "", projectName: "" };
              candidate.object = { _id: "", objectName: "" };
              candidate.part = { _id: "", partName: "" };
            }
            candidate.company = result.match;
            matchedCount += 1;
            if (result.warning) warnings.push(result.warning);
          } else {
            warnings.push(result.warning);
            resolutionFailed = true;
          }
        }

        if (location.object) {
          if (resolutionFailed || !candidate.company._id) {
            warnings.push(
              `Обект „${location.object}“ не може да бъде избран без разпознат проект.`
            );
            resolutionFailed = true;
          } else {
            loadedOptions.projects = await this.fetchAiLocationOptions(
              `projects/${candidate.company._id}/${this.getUser._id}`
            );
            const result = findAiLocationMatch(
              location.object,
              loadedOptions.projects,
              "projectName",
              "обект"
            );

            if (result.match) {
              if (hasChanged(candidate.project, result.match)) {
                candidate.object = { _id: "", objectName: "" };
                candidate.part = { _id: "", partName: "" };
              }
              candidate.project = result.match;
              matchedCount += 1;
              if (result.warning) warnings.push(result.warning);
            } else {
              warnings.push(result.warning);
              resolutionFailed = true;
            }
          }
        }

        if (location.subobject) {
          if (resolutionFailed || !candidate.project._id) {
            warnings.push(
              `Подобект „${location.subobject}“ не може да бъде избран без разпознат обект.`
            );
            resolutionFailed = true;
          } else {
            loadedOptions.objects = await this.fetchAiLocationOptions(
              `objects/${candidate.company._id}/${candidate.project._id}/${this.getUser._id}`
            );
            const result = findAiLocationMatch(
              location.subobject,
              loadedOptions.objects,
              "objectName",
              "подобект"
            );

            if (result.match) {
              if (hasChanged(candidate.object, result.match)) {
                candidate.part = { _id: "", partName: "" };
              }
              candidate.object = result.match;
              matchedCount += 1;
              if (result.warning) warnings.push(result.warning);
            } else {
              warnings.push(result.warning);
              resolutionFailed = true;
            }
          }
        }

        if (location.part) {
          if (resolutionFailed || !candidate.object._id) {
            warnings.push(
              `Част „${location.part}“ не може да бъде избрана без разпознат подобект.`
            );
            resolutionFailed = true;
          } else {
            loadedOptions.parts = await this.fetchAiLocationOptions(
              `parts/${candidate.company._id}/${candidate.project._id}/${candidate.object._id}/${this.getUser._id}`
            );
            const result = findAiLocationMatch(
              location.part,
              loadedOptions.parts,
              "partName",
              "част"
            );

            if (result.match) {
              candidate.part = result.match;
              matchedCount += 1;
              if (result.warning) warnings.push(result.warning);
            } else {
              warnings.push(result.warning);
              resolutionFailed = true;
            }
          }
        }
      } catch (error) {
        console.log(error);
        resolutionFailed = true;
        warnings.push(
          "Локацията не можа да бъде проверена спрямо достъпните обекти."
        );
      }

      if (resolutionFailed) {
        return { matchedCount: 0, warnings };
      }

      if (location.project) this.setAiReportProject(candidate.company);
      if (location.object) this.setAiReportObject(candidate.project);
      if (location.subobject) {
        this.setAiReportSubobject(candidate.object);
        await this.getSelectedObject(candidate.object._id);
        this.getReportSuggestions();
      }
      if (location.part) this.report.part = candidate.part;

      if (loadedOptions.companies) this.companies = loadedOptions.companies;
      if (loadedOptions.projects) this.projects = loadedOptions.projects;
      if (loadedOptions.objects) this.objects = loadedOptions.objects;
      if (loadedOptions.parts) this.parts = loadedOptions.parts;

      return { matchedCount, warnings };
    },
    async fetchAiLocationOptions(path) {
      const response = await this.$http.get(
        `${process.env.VUE_APP_BASE_URI}/company/reporters/${path}`
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    setAiReportProject(company) {
      const hasChanged =
        String(this.report.company._id || "") !== String(company._id || "");
      this.report.company = company;

      if (!hasChanged) return;

      this.report.project = { _id: "", projectName: "" };
      this.report.object = { _id: "", objectName: "" };
      this.report.part = { _id: "", partName: "" };
      this.projects = [];
      this.objects = [];
      this.parts = [];
      this.selectedObject = undefined;
      this.reportSuggestions = {};
    },
    setAiReportObject(project) {
      const hasChanged =
        String(this.report.project._id || "") !== String(project._id || "");
      this.report.project = project;

      if (!hasChanged) return;

      this.report.object = { _id: "", objectName: "" };
      this.report.part = { _id: "", partName: "" };
      this.objects = [];
      this.parts = [];
      this.selectedObject = undefined;
      this.reportSuggestions = {};
    },
    setAiReportSubobject(object) {
      const hasChanged =
        String(this.report.object._id || "") !== String(object._id || "");
      this.report.object = object;

      if (!hasChanged) return;

      this.report.part = { _id: "", partName: "" };
      this.parts = [];
      this.selectedObject = undefined;
      this.reportSuggestions = {};
    },
    createAiReportRow(section, sourceRow) {
      const source =
        sourceRow && typeof sourceRow === "object" ? sourceRow : {};

      return section.fields.reduce((row, field) => {
        if (field.calculated) return row;

        const value = source[field.key];
        if (field.type === "number") {
          const number = Number(value);
          row[field.key] =
            value !== null && value !== "" && Number.isFinite(number)
              ? number
              : "";
        } else {
          row[field.key] =
            value === null || value === undefined ? "" : String(value);
        }

        return row;
      }, {});
    },
    hasDiaryRowValue(section, row) {
      return section.fields.some((field) => {
        if (field.calculated) return false;
        const value = row[field.key];
        return value !== "" && value !== null && value !== undefined;
      });
    },
    canSeePriceFields() {
      const permissions = this.getUser.permissions || {};
      return permissions.isSuperAdmin || permissions.isAdmin;
    },
    visibleSectionFields(section) {
      return section.fields.filter((field) => {
        return !field.requiresPriceRole || this.canSeePriceFields();
      });
    },
    toNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    calculatedDiaryValue(row, field) {
      const value = this.calculateDiaryFieldValue(row, field);
      if (value === null || (value === 0 && !field.showZero)) return "";
      return value.toFixed(2);
    },
    calculateDiaryFieldValue(row, field) {
      if (field.calculated === "sum") {
        const priceKey = field.priceKey || "price";
        return (
          this.toNumber(row[priceKey]) * this.toNumber(row[field.quantityKey])
        );
      }

      if (field.calculated === "difference") {
        return (
          this.toNumber(row[field.baseKey]) -
          this.toNumber(row[field.quantityKey])
        );
      }

      return null;
    },
    openSignatureModal() {
      if (this.allowSave) {
        this.isSignatureModalOpened = true;
      }
    },
    closeSignatureModal() {
      this.isSignatureModalOpened = false;
    },
    setSignature(image, hasChanged) {
      this.report.signature = image;

      if (hasChanged) {
        this.reportChanged();
      }
    },
    createReport() {
      return {
        date: new Date(),
        createdBy: {
          _id: "",
          name: "",
        },
        company: {
          _id: "",
          companyName: "",
        },
        project: {
          _id: "",
          projectName: "",
        },
        object: {
          _id: "",
          objectName: "",
        },
        part: {
          _id: "",
          partName: "",
        },
        weather: emptyWeather(),
        diary: emptyDiary(),
        invoices: [],
        signature: "",
        isInvalid: false,
      };
    },
    normalizeReport(report) {
      const normalized = Object.assign(this.createReport(), report);
      normalized.createdBy = Object.assign(
        this.createReport().createdBy,
        report.createdBy || {}
      );
      normalized.company = Object.assign(
        this.createReport().company,
        report.company || {}
      );
      normalized.project = Object.assign(
        this.createReport().project,
        report.project || {}
      );
      normalized.object = Object.assign(
        this.createReport().object,
        report.object || {}
      );
      normalized.part = Object.assign(
        this.createReport().part,
        report.part || {}
      );
      normalized.weather = Object.assign(emptyWeather(), report.weather || {});
      normalized.weather.location = Object.assign(
        emptyObjectLocation(),
        report.weather && report.weather.location ? report.weather.location : {}
      );
      normalized.diary = Object.assign(emptyDiary(), report.diary || {});
      normalized.invoices = Array.isArray(report.invoices)
        ? report.invoices
        : [];
      normalized.signature = report.signature || "";
      normalized.date = report.date ? new Date(report.date) : new Date();
      return normalized;
    },
    selectCompany(company) {
      this.resetSelectedProject();
      this.report.company = company;
      this.getProjects(true);
      this.getReportSuggestions();
      this.reportChanged();
    },
    selectProject(project) {
      this.resetSelectedObject();
      this.report.project = project;
      this.getObjects(true);
      this.getReportSuggestions();
      this.reportChanged();
    },
    selectObject(object) {
      this.resetSelectedPart();
      this.report.object = object;
      this.getSelectedObject(object._id);
      this.getParts();
      this.getReportSuggestions();
      this.reportChanged();

      setTimeout(() => {
        this.fetchWeatherForecast();
      }, 500);
    },
    selectPart(part) {
      this.report.part = part;
      this.getReportSuggestions();
      this.reportChanged();
    },
    resetSelectedCompany() {
      this.report.company = { _id: "", companyName: "" };
      this.projects = [];
      this.resetSelectedProject();
      this.getReportSuggestions();
      this.reportChanged();
    },
    resetSelectedProject() {
      this.projects = [];
      this.report.project = { _id: "", projectName: "" };
      this.resetSelectedObject();
      this.getReportSuggestions();
      this.reportChanged();
    },
    resetSelectedObject() {
      this.objects = [];
      this.report.object = { _id: "", objectName: "" };
      this.selectedObject = undefined;
      this.resetSelectedPart();
      this.getReportSuggestions();
      this.reportChanged();
    },
    resetSelectedPart() {
      this.parts = [];
      this.report.part = { _id: "", partName: "" };
      this.getReportSuggestions();
      this.reportChanged();
    },
    setReportDate(value) {
      this.report.date = value ? new Date(value) : new Date();
      this.reportChanged();
    },
    setWeatherCondition(condition) {
      this.$set(this.report.weather, "condition", condition);
      this.reportChanged();
    },
    weatherForecastDate() {
      return this.$moment(this.report.date).format("YYYY-MM-DD");
    },
    fetchWeatherForecast() {
      const location = this.report.weather.location || emptyObjectLocation();
      const latitude = location.latitude;
      const longitude = location.longitude;

      if (!latitude || !longitude) {
        this.$snotify.error("Обекта няма избрана локация");
        return;
      }

      this.isFetchingWeather = true;
      this.$http
        .get(`${process.env.VUE_APP_BASE_URI}/weather/forecast`, {
          params: {
            latitude,
            longitude,
            date: this.weatherForecastDate(),
            hour: new Date().getHours(),
          },
        })
        .then((response) => {
          this.report.weather = Object.assign(
            {},
            this.report.weather,
            response.data,
            { location: Object.assign(emptyObjectLocation(), location) }
          );
          this.reportChanged();
          this.$snotify.success("Климатичните условия са засечени успешно.");
        })
        .catch((error) => {
          const message =
            error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Неуспешно засичане на климатични условия.";
          this.$snotify.error(message);
        })
        .then(() => {
          this.isFetchingWeather = false;
        });
    },
    setDiaryValue(sectionKey, index, fieldKey, value) {
      this.$set(this.report.diary[sectionKey][index], fieldKey, value);
      this.reportChanged();
    },
    openInvoiceScanner() {
      if (!this.hasSelectedLocation) {
        this.$snotify.error("Изберете проект, обект и подобект.");
        return;
      }

      this.isInvoiceScannerOpened = true;
    },
    closeInvoiceScanner() {
      if (!this.isInvoiceScanProcessing) this.isInvoiceScannerOpened = false;
    },
    invoiceItemPrice(value, currency) {
      const price = Number(value);
      if (!Number.isFinite(price)) return "";

      const converted =
        String(currency || "").toUpperCase() === "BGN"
          ? price / 1.95583
          : price;

      return Number(converted.toFixed(6));
    },
    addInvoiceToReport(result) {
      const savedInvoice = result && result.savedInvoice;
      const invoice = result && result.invoice;

      if (!savedInvoice || !savedInvoice._id || !invoice) {
        this.$snotify.error(
          "Фактурата е разчетена, но липсва референция към записа ѝ."
        );
        return;
      }

      const alreadyAdded = this.report.invoices.some(
        (item) => String(item._id) === String(savedInvoice._id)
      );

      if (alreadyAdded) return;

      this.report.invoices.push({
        _id: savedInvoice._id,
        number: String(invoice.number || ""),
      });

      const items = Array.isArray(invoice.items) ? invoice.items : [];
      const rows = items
        .filter((item) => item && item.name)
        .map((item) => {
          const quantity = Number(item.quantity);
          const price = this.invoiceItemPrice(item.price, invoice.currency);
          const hasQuantity = Number.isFinite(quantity);
          const hasPrice = Number.isFinite(Number(price));

          return {
            name: item.name,
            documentNumber: String(invoice.number || ""),
            unit: item.unit || "",
            incomingQuantity: hasQuantity ? quantity : "",
            source: "",
            place: "",
            note: "",
            price,
            totalSum: hasQuantity && hasPrice ? quantity * Number(price) : 0,
          };
        });

      this.report.diary.buildingMaterials.push(...rows);
      this.$set(this.collapsedGroups, "costs", false);
      this.$set(this.collapsedSections, "buildingMaterials", false);
      this.reportChanged();

      if (rows.length) {
        this.$snotify.success(
          `Добавени са ${rows.length} артикула от фактура ${invoice.number}.`
        );
      } else {
        this.$snotify.warning(
          `Фактура ${invoice.number} е добавена, но няма разпознати артикули.`
        );
      }
    },
    invoiceSearchUrl(number) {
      if (!number || !process.env.VUE_APP_PAYMENTS_URL) return "";

      const linked = this.report.invoices.some(
        (invoice) => String(invoice.number) === String(number)
      );
      if (!linked) return "";

      const baseUrl = process.env.VUE_APP_PAYMENTS_URL.replace(/\/$/, "");
      return `${baseUrl}/invoices?search=${encodeURIComponent(number)}`;
    },
    toggleGroup(groupKey) {
      this.$set(
        this.collapsedGroups,
        groupKey,
        !this.collapsedGroups[groupKey]
      );
    },
    toggleSection(sectionKey) {
      this.$set(
        this.collapsedSections,
        sectionKey,
        !this.collapsedSections[sectionKey]
      );
    },
    isGroupCollapsed(groupKey) {
      return !!this.collapsedGroups[groupKey];
    },
    isSectionCollapsed(sectionKey) {
      return !!this.collapsedSections[sectionKey];
    },
    addRow(sectionKey) {
      const section = diarySections.find((item) => item.key === sectionKey);
      const row = section.fields.reduce((result, field) => {
        if (field.calculated) return result;
        result[field.key] = "";
        return result;
      }, {});
      this.report.diary[sectionKey].push(row);
      this.reportChanged();
    },
    removeRow(sectionKey, index) {
      const removeText = "Сигурни ли сте, че искате да изтриете реда?";
      if (!confirm(removeText)) return;

      this.report.diary[sectionKey].splice(index, 1);
      this.reportChanged();
    },
    fieldSuggestions(sectionKey, fieldKey) {
      return this.reportSuggestions[`${sectionKey}.${fieldKey}`] || [];
    },
    getCompanies() {
      const id = this.getUser._id;
      const requestUrl = `${process.env.VUE_APP_BASE_URI}/company/reporters/companies/${id}`;
      this.$http
        .get(requestUrl)
        .then((response) => {
          this.companies = response.data;
        })
        .catch((error) => console.log(error));
    },
    getProjects(autoselect) {
      this.projects = [];
      if (this.report.company._id.length) {
        const id = this.getUser._id;
        const requestUrl = `${process.env.VUE_APP_BASE_URI}/company/reporters/projects/${this.report.company._id}/${id}`;
        this.$http
          .get(requestUrl)
          .then((response) => {
            this.projects = response.data;
            if (autoselect && this.projects.length === 1) {
              this.selectProject(this.projects[0]);
            }
          })
          .catch((error) => console.log(error));
      }
    },
    getObjects(autoselect) {
      this.objects = [];
      if (this.report.project._id.length) {
        const id = this.getUser._id;
        const requestUrl = `${process.env.VUE_APP_BASE_URI}/company/reporters/objects/${this.report.company._id}/${this.report.project._id}/${id}`;
        this.$http
          .get(requestUrl)
          .then((response) => {
            this.objects = response.data;
            if (autoselect && this.objects.length === 1) {
              this.selectObject(this.objects[0]);
            }
          })
          .catch((error) => console.log(error));
      }
    },
    getParts() {
      this.parts = [];
      if (this.report.project._id.length) {
        const id = this.getUser._id;
        const requestUrl = `${process.env.VUE_APP_BASE_URI}/company/reporters/parts/${this.report.company._id}/${this.report.project._id}/${this.report.object._id}/${id}`;
        this.$http
          .get(requestUrl)
          .then((response) => {
            this.parts = response.data;
          })
          .catch((error) => console.log(error));
      }
    },
    getSelectedObject(id) {
      this.selectedObject = undefined;
      const url = `${process.env.VUE_APP_BASE_URI}/company/object/${id}`;
      return this.$http
        .get(url)
        .then((response) => {
          this.selectedObject = response.data[0];
          this.$set(
            this.report.weather,
            "location",
            Object.assign(
              emptyObjectLocation(),
              this.selectedObject.location || this.report.weather.location
            )
          );
        })
        .catch((error) => console.log(error));
    },
    getReportSuggestions() {
      if (!this.report.object || !this.report.object._id) {
        this.reportSuggestions = {};
        return;
      }
      const url = `${process.env.VUE_APP_BASE_URI}/report/suggestions`;

      this.$http
        .get(url)
        .then((response) => {
          this.reportSuggestions = response.data || {};
        })
        .catch((error) => console.log(error));
    },
    getReport(id) {
      const url = `${process.env.VUE_APP_BASE_URI}/report/${id}`;
      this.$http
        .get(url)
        .then((response) => {
          this.report = this.normalizeReport(response.data);
          this.initialObjectId = this.report.object._id;
          this.getReportSuggestions();
          this.getSelectedObject(response.data.object._id).then(() => {
            if (
              this.report.isInvalid &&
              !this.getUser.permissions.isSuperAdmin &&
              !this.getUser.permissions.isAdmin
            ) {
              this.allowSave = false;
              this.$snotify.warning(
                "Грешните отчети могат да бъдат редактирани само от администратори!"
              );
            }
            this.getParts();
          });
        })
        .catch((error) => console.log(error));
    },
    isValid() {
      if (!this.report.company._id.length) {
        this.$snotify.error("Не сте избрали проект!");
        return false;
      }
      if (!this.report.project._id.length) {
        this.$snotify.error("Не сте избрали обект!");
        return false;
      }
      if (!this.report.object._id.length) {
        this.$snotify.error("Не сте избрали подобект!");
        return false;
      }
      return true;
    },
    prepareReport() {
      this.report.createdBy._id = this.getUser._id;
      this.report.createdBy.name = this.getUser.name;
      if (this.report.part && !this.report.part._id) delete this.report.part;
      diarySections.forEach((section) => {
        (this.report.diary[section.key] || []).forEach((row) => {
          section.fields.forEach((field) => {
            if (field.calculated) {
              const value = this.calculateDiaryFieldValue(row, field);
              this.$set(row, field.key, value || 0);
            }
          });
        });
      });
      if (this.selectedObject && this.selectedObject.location) {
        this.report.objectLocation = Object.assign(
          emptyObjectLocation(),
          this.selectedObject.location
        );
      }
      this.report.objectWeather = {
        location: Object.assign(
          emptyObjectLocation(),
          this.report.objectLocation
        ),
        weather: Object.assign(emptyWeather(), this.report.weather),
      };
    },
    postReport() {
      if (!this.isValid()) return;
      this.prepareReport();
      const url = `${process.env.VUE_APP_BASE_URI}/report`;
      this.$http
        .post(url, this.report)
        .then((response) => {
          if (response.data.errors) {
            this.$snotify.error("Нещо се обърка! Моля опитайте отново.");
            return;
          }
          this.$snotify.success("Дневникът на обекта беше създаден успешно!");
          this.newLog(
            "newReport",
            `подобект - ${this.report.object.objectName}`
          );
          this.isReportChanged = false;
          this.cancelReport();
        })
        .catch((error) => {
          console.log(error.response);
          this.$snotify.error(
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
              "Нещо се обърка! Моля опитайте отново."
          );
        });
    },
    editReport() {
      if (!this.isValid()) return;
      this.prepareReport();
      const url = `${process.env.VUE_APP_BASE_URI}/report/${this.$route.params.id}`;
      this.$http
        .put(url, this.report)
        .then(() => {
          this.$snotify.success("Промените са записани успешно!");
          this.newLog(
            "editReport",
            `подобект - ${this.report.object.objectName}`
          );
          this.isReportChanged = false;
          this.cancelReport();
        })
        .catch((error) => {
          console.log(error);
          this.$snotify.error(
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
              "Нещо се обърка! Моля опитайте отново."
          );
        });
    },
    save() {
      if (this.$route.name === "newReport") this.postReport();
      if (this.$route.name === "editReport") this.editReport();
    },
    cancelReport() {
      if (this.isComingFromOutside) {
        this.$router.push("/reports/own");
      } else {
        this.$router.go(-1);
      }
    },
    reportChanged() {
      this.isReportChanged = true;
    },
    checkForUnsavedData(next) {
      this.isUnsavedChangesModalOpened = true;
      this.$once("closeUnsavedChangesModal", (answer) => {
        if (answer) {
          next(false);
          this.save();
        } else {
          next();
        }
        this.isUnsavedChangesModalOpened = false;
        this.isReportChanged = false;
      });
    },
    beforeUnload(event) {
      if (this.isReportChanged) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    exportPdf() {
      if (!this.report._id || this.isExportingPdf) return;

      this.isExportingPdf = true;
      this.$http
        .get(`${process.env.VUE_APP_BASE_URI}/report/${this.report._id}/pdf`, {
          responseType: "blob",
        })
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/pdf",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          const date = this.$moment(this.report.date).format("YYYY-MM-DD");

          link.href = url;
          link.download = `report-${date}-${this.report._id}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.log(error);
          this.$snotify.error("Неуспешен експорт на PDF.");
        })
        .then(() => {
          this.isExportingPdf = false;
        });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.isComingFromOutside = from.name ? false : true;
    });
  },
  mounted() {
    this.getCompanies();
    if (this.$route.name === "editReport") {
      this.getReport(this.$route.params.id);
    } else {
      this.report.createdBy._id = this.getUser._id;
      this.report.createdBy.name = this.getUser.name;
    }
    window.addEventListener("beforeunload", this.beforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.isReportChanged) {
      this.checkForUnsavedData(next);
    } else {
      next();
    }
  },
  destroyed() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },
};
</script>

<style scoped lang="scss">
.report-diary {
  background: var(--color-page-bg);
  padding: 10px 40px 30px;
}

.report-diary__toolbar,
.report-diary__title,
.report-diary .btns {
  align-items: center;
  display: flex;
}

.report-diary__toolbar {
  justify-content: space-between;
  margin-bottom: 10px;
}

.report-diary__title h2 {
  font-size: 16px;
  margin-left: 8px;
}

.report-diary__export-button {
  .sienit2 {
    font-size: 14px;
  }
}

.report-diary__card {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
}

.report-diary__top-card {
  background: #f4f4f4;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  margin-bottom: 12px;
  padding: 8px 26px 12px;
}

.report-diary__automation-actions {
  display: grid;
  gap: var(--gap);
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 10px;

  .btn {
    align-items: center;
    background: linear-gradient(90deg, #d38643 0%, #df8a08 100%);
    min-height: 38px;
    margin: initial;
    display: flex;
    gap: var(--gap);
    font-size: 12px;
    font-weight: 700;
    transition: var(--transition);
    width: initial;

    &.btn--voice-report {
      background: linear-gradient(270deg, #457c44 0%, #56be54 100%);
    }

    &:hover:not(:disabled) {
      opacity: 0.8;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
    .icon {
      flex: 0 0 auto;
      height: 22px;
      width: 63px;
    }
  }
}

.report-diary__top-main {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) 300px;
}

.report-diary__meta {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: max-content 150px;
}

.report-diary__heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;

  h1 {
    color: #25364c;
    font-size: 22px;
    line-height: 1.1;
    text-transform: uppercase;
  }
}

.report-diary__status {
  color: var(--color-danger);
  font-weight: 700;
}

.report-diary__section {
  border: 1px solid var(--color-border);
  border-radius: 6px;
  margin-bottom: 12px;
  overflow: visible;
}

.report-diary__section--income {
  background: #f4fbf3;
  border-color: #b8d9bb;
}

.report-diary__section-header {
  align-items: center;
  border: none;
  border-radius: 5px 5px 0 0;
  background: var(--color-danger);
  color: var(--color-on-main);
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  justify-content: space-between;
  padding: 7px 10px;
  text-transform: uppercase;
  width: 100%;
}

.report-diary__section-header--income {
  background: var(--color-success);

  .report-diary__collapse-toggle {
    color: var(--color-success);
  }
}

.report-diary__collapse-toggle {
  align-items: center;
  background: var(--color-surface);
  border-radius: 3px;
  color: var(--color-danger);
  display: inline-flex;
  font-size: 17px;
  font-weight: 700;
  height: 17px;
  justify-content: center;
  line-height: 1;
  width: 17px;
}

.report-diary__collapse-toggle--inner {
  background: #333;
  color: var(--color-on-main);
  font-size: 13px;
  height: 14px;
  width: 14px;
}

.report-diary__form-grid {
  display: grid;
  gap: 8px 12px;
  grid-template-columns: repeat(2, auto);
  padding: 0 18px 0 0;
  position: relative;

  &::before {
    background: #cfc6b2;
    content: "";
    height: 50%;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 1px;
  }
}

.report-diary__weather {
  align-self: start;
  color: #394b60;
  padding-top: 7px;
}

.report-diary__weather-header {
  align-items: center;
  display: flex;
  font-size: 11px;
  justify-content: space-between;
  margin-bottom: 5px;
  text-transform: uppercase;

  strong {
    color: #34475e;
    font-size: 12px;
  }

  label {
    align-items: center;
    color: #7b8794;
    display: flex;
    gap: 3px;
  }

  input {
    background: transparent;
    border: none;
    color: #34475e;
    font-size: 12px;
    font-weight: 700;
    padding: 0;
    text-align: right;
    width: 34px;
  }
}

.report-diary__weather-auto {
  align-items: center;
  background: #f4fff1;
  border: 1px dashed #2563eb;
  border-radius: 3px;
  color: #97cb59;
  cursor: pointer;
  display: flex;
  font-size: 11px;
  font-weight: 700;
  justify-content: center;
  margin-bottom: 6px;
  padding: 5px 8px;
  width: 100%;

  img {
    flex: 0 0 auto;
    height: 16px;
    margin-right: 6px;
    width: 14px;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
}

.report-diary__weather-options {
  display: grid;
  gap: var(--gap);
  grid-template-columns: repeat(4, 1fr);
}

.report-diary__weather-option {
  align-items: center;
  background: #f8fbff;
  border: 1px solid #c9d8e7;
  border-radius: 3px;
  color: #34475e;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-size: 11px;
  gap: 2px;
  justify-content: space-between;
  min-height: 58px;
  padding: 4px 3px 2px;

  img {
    height: 38px;
    object-fit: contain;
    width: 44px;
  }
}

.report-diary__weather-option--active {
  background: #0b8fc9;
  border-color: #0b8fc9;
  color: var(--color-on-main);
  font-weight: 700;
}

.report-diary__top-card ::v-deep .base-field {
  label {
    color: #5c6d82;
  }

  .new-input {
    margin: 4px 0 0;

    input {
      background: #f8fbff;
      border-color: #c8d7e6;
      min-height: 34px;
    }
  }
}

.report-diary__table-wrap {
  background: #fbfaf5;
  border-bottom: 1px solid #d9d4c1;
  padding: 0;

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 5px 5px;
  }
}

.report-diary__table-wrap--income {
  background: #f4fbf3;
  border-bottom-color: #cfe4d1;

  .report-diary__table-title {
    background: #dcefe2;
    color: #31445b;
  }

  .report-diary__table-content {
    background: #f4fbf3;
  }

  .report-diary__table th {
    background: #eef3f8;
  }
}

.report-diary__table-title {
  align-items: center;
  background: #ece9d8;
  border: none;
  color: #31445b;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 8px 10px;
  text-align: left;
  text-transform: uppercase;
  width: 100%;
}

.report-diary__table-content {
  background: #fbfaf5;
  padding: 8px 26px 10px;
}

.report-diary__add-row {
  background: var(--color-surface);
  border: 2px dashed #2563eb;
  color: #2975c9;
  font-size: 11px;
  font-weight: bold;
  padding: 7px 13px;
  margin-top: 8px;
}

.report-diary__table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;

  th,
  td {
    border: 1px solid #d7e1eb;
    font-size: 12px;
    padding: 0;
  }

  th {
    background: #edf3f8;
    color: #4d6177;
    font-size: 11px;
    padding: 9px 10px;
    text-align: left;
  }
}

.report-diary__table-main-field {
  width: 25%;
}

.report-diary__table-number {
  text-align: center;
  width: 45px;
}

td.report-diary__table-number {
  padding: 0 8px;
}

.report-diary__table-actions {
  text-align: center;
  width: 44px;
}

.report-diary__table-remove {
  background: transparent;
  border: none;
  color: #e54949;
  cursor: pointer;
}

.report-diary__invoice-link {
  color: #0078d7;
  font-weight: 700;
  text-decoration: underline;
}

.report-diary ::v-deep .modal.report-diary__scanner-modal {
  background: #fff;
  border-radius: 8px;
  max-height: calc(100vh - 48px);
  max-width: 1120px;
  overflow: auto;
  padding: 0;
  position: relative;
  width: calc(100% - 48px);
}

.report-diary__scanner-close {
  align-items: center;
  background: #fff;
  border: 1px solid #d8d2bd;
  border-radius: 50%;
  color: #373737;
  cursor: pointer;
  display: flex;
  font-size: 24px;
  height: 34px;
  justify-content: center;
  line-height: 1;
  position: absolute;
  right: 10px;
  top: 10px;
  width: 34px;
  z-index: 2;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.report-diary__table td.report-diary__empty-row {
  color: #77828b;
  padding: 12px;
  text-align: center;
}

.report-diary__signature {
  align-items: center;
  background: #f7f7f7;
  border-color: var(--color-border);
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  padding: 18px 12px 18px 28px;
}

.report-diary__signature-author h3 {
  color: #25364c;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.report-diary__signature-author label {
  color: #5f6e81;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 5px;
}

.report-diary__signature-author p {
  align-items: center;
  background: #fbfdff;
  border: 1px solid #c8d6e5;
  border-radius: 3px;
  color: #34475e;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  height: 34px;
  min-width: 168px;
  padding: 0 12px;
}

.report-diary__signature-box {
  align-items: center;
  background: var(--color-surface);
  border: 1px solid #1d1d1d;
  border-radius: 5px;
  color: #31445b;
  cursor: pointer;
  display: flex;
  height: 70px;
  justify-content: center;
  padding: 5px 20px;
  width: 318px;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  img {
    display: block;
    height: 100%;
    max-width: 100%;
    object-fit: contain;
    width: 100%;
  }
}

@media screen and (max-width: 1000px) {
  .report-diary {
    padding: 10px 15px 28px;
  }

  .report-diary__toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: calc(var(--gap) + 7px);
  }

  .report-diary__title {
    min-width: 0;
    width: 100%;

    h2 {
      font-size: 18px;
      margin: 0 0 0 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .report-diary__toolbar > .btns,
  .report-diary__card > .btns {
    display: grid;
    gap: 7px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 100%;
  }

  .report-diary__toolbar ::v-deep .btn,
  .report-diary__card > .btns ::v-deep .btn {
    margin: 0;
    min-width: 0;
    padding: 0 8px;

    &:not(.new-content-data-item-toggle-circle) {
      width: 100%;
    }
  }

  .report-diary__top-card {
    border-radius: 10px;
    padding: 12px;
  }

  .report-diary__heading h1 {
    font-size: 19px;
  }

  .report-diary__form-grid,
  .report-diary__meta {
    gap: calc(var(--gap) + var(--gap));
    grid-template-columns: 1fr;
  }

  .report-diary__form-grid {
    padding-right: 0;

    &::before {
      display: none;
    }
  }

  .report-diary__automation-actions {
    grid-template-columns: 1fr;
  }

  .report-diary__top-main {
    gap: 14px;
    grid-template-columns: 1fr;
  }

  .report-diary__weather {
    border-top: 1px solid var(--color-border);
    padding-top: 12px;
    width: 100%;
  }

  .report-diary__weather-auto {
    min-height: 38px;
  }

  .report-diary__section {
    border-radius: 10px;
    margin-bottom: 10px;
  }

  .report-diary__section-header {
    border-radius: 9px 9px 0 0;
    font-size: 12px;
    min-height: 40px;
    padding: 8px 12px;
    text-align: left;
  }

  .report-diary__table-title {
    font-size: 12px;
    gap: calc(var(--gap) + var(--gap));
    min-height: 42px;
    padding: 9px 12px;
  }

  .report-diary__table-title strong {
    line-height: 1.25;
  }

  .report-diary__table-content {
    padding: 10px;
  }

  .report-diary__signature-box {
    max-width: 100%;
    width: 100%;
  }

  .report-diary__table-wrap {
    overflow: visible;
  }

  .report-diary__table {
    display: block;
    min-width: 0;
    table-layout: auto;

    thead {
      display: none;
    }

    tbody {
      display: grid;
      gap: calc(var(--gap) + var(--gap));
    }

    th,
    td {
      border: none;
    }
  }

  .report-diary__data-row {
    background: var(--color-report-bg);
    border: 1px solid var(--color-report-border);
    border-radius: 12px;
    box-shadow: 0 2px 7px rgba(41, 16, 3, 0.06);
    display: grid;
    padding: 46px 10px 8px;
    position: relative;
  }

  .report-diary__data-row > td {
    align-items: center;
    border-bottom: 1px solid var(--color-report-border);
    display: grid;
    gap: calc(var(--gap) + var(--gap));
    grid-template-columns: minmax(105px, 34%) minmax(0, 1fr);
    min-height: 44px;
    padding: 5px 0;
    text-align: right;
    width: auto;

    &::before {
      color: var(--color-report-text);
      content: attr(data-label);
      font-size: 11px;
      font-weight: 700;
      line-height: 1.2;
      text-align: left;
      text-transform: uppercase;
    }
  }

  .report-diary__data-row > td.report-diary__table-number {
    align-items: center;
    background: var(--color-report-accent);
    border: none;
    border-radius: 50%;
    color: var(--color-on-main);
    display: flex;
    font-size: 13px;
    font-weight: 700;
    height: 28px;
    justify-content: center;
    left: 10px;
    min-height: 28px;
    padding: 0;
    position: absolute;
    top: 9px;
    width: 28px;

    &::before {
      display: none;
    }
  }

  .report-diary__data-row > td.report-diary__table-actions {
    border: none;
    display: flex;
    min-height: 36px;
    padding: 0;
    position: absolute;
    right: 6px;
    top: 5px;
    width: 36px;

    &::before {
      display: none;
    }
  }

  .report-diary__table-remove {
    font-size: 17px;
  }

  .report-diary__data-row ::v-deep .base-field--table-input {
    min-width: 0;

    .base-field__input {
      background: var(--color-surface);
      border: 1px solid var(--color-report-border);
      border-radius: 6px;
      min-height: 36px;
      text-align: right;
    }
  }

  .report-diary__empty-data-row {
    display: block;
  }

  .report-diary__empty-data-row .report-diary__empty-row {
    background: var(--color-report-bg);
    border: 1px dashed var(--color-report-border);
    border-radius: 10px;
    display: block;
    width: 100%;
  }

  .report-diary__add-row {
    border-color: var(--color-report-accent);
    color: var(--color-report-accent);
    margin: 10px 0 0;
    min-height: 40px;
    width: 100%;
  }

  .report-diary__signature {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .report-diary__signature-author p {
    min-width: 0;
    width: 100%;
  }
}

@media screen and (max-width: 520px) {
  .report-diary {
    padding: 8px 10px 24px;
  }

  .report-diary__weather-header {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--gap);
  }

  .report-diary__weather-options {
    grid-template-columns: repeat(2, 1fr);
  }

  .report-diary__weather-option {
    min-height: 66px;
  }

  .report-diary__data-row > td {
    gap: 8px;
    grid-template-columns: minmax(92px, 35%) minmax(0, 1fr);
  }

  .report-diary__toolbar ::v-deep .btn,
  .report-diary__card > .btns ::v-deep .btn {
    font-size: 11px;
    padding: 0 5px;
  }

  .report-diary__toolbar ::v-deep .btn .sienit,
  .report-diary__toolbar ::v-deep .btn .sienit2,
  .report-diary__card > .btns ::v-deep .btn .sienit,
  .report-diary__card > .btns ::v-deep .btn .sienit2 {
    font-size: 13px;
    margin-right: 4px;
  }
}
</style>

